import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  ActionButtons,
  Button,
  DataTable,
  Modal,
  type DataTableColumn,
  type DataTableSortDirection,
} from '@enamelai/design-system'
import { Icon as ArrowDownIcon } from '@enamelai/design-system/icons/Arrow_3_down'
import { Icon as ArrowUpIcon } from '@enamelai/design-system/icons/Arrow_3_up'
import { Icon as DeleteIcon } from '@enamelai/design-system/icons/Delete2'
import { DeleteConfirmationModal } from '@components/layout/Modals'
import { StaffScope } from '@constants/Settings/staffPermission.const'
import { useNotificationToast } from '@context/NotificationToastContext'
import type {
  FormGroup,
  FormGroupItem,
  IFormTemplate,
} from '@dataTypes/forms.type'
import { FormTemplateStatus } from '@dataTypes/forms.type'
import { useSettingsHeader } from '@pages/Settings/SettingsHeader.context'
import { useSettingsSubpageLoading } from '@pages/Settings/SettingsSubpageGate.component'
import {
  createFormGroup,
  deleteFormGroup,
  getFormGroups,
  getFormTemplates,
  setNewPatientKioskFormGroup,
  updateFormGroup,
} from '@services/api/forms.api'
import { useAppSelector } from '@store/hooks'
import {
  createFailedToast,
  createSuccessToast,
} from '@utils/notificationToast.util'
import { hasStaffScope } from '@utils/staffScope.util'
import './PatientFormGroups.css'

const DEFAULT_PACKET_NAME = 'New Patient Kiosk Forms'
const DEFAULT_PACKET_DESCRIPTION =
  'Forms assigned automatically after kiosk registration.'

type PersistReason = 'add' | 'remove' | 'reorder'

type PacketFormRow = {
  id: string
  templateKey: string
  order: number
  title: string
  category: string
  versionLabel: string
  available: boolean
}

type ApprovedFormRow = {
  id: string
  templateKey: string
  title: string
  category: string
  versionLabel: string
}

const chooseKioskGroup = (groups: FormGroup[]): FormGroup | undefined =>
  groups.find((group) => group.isNewPatientKioskDefault) ?? groups[0]

const versionLabel = (version: number | null | undefined): string =>
  version != null ? String(version) : '—'

const PatientFormGroups = (): React.ReactElement => {
  const { setHeaderButtons } = useSettingsHeader()
  const { showToast } = useNotificationToast()
  const clinicId = useAppSelector(
    (state) => state.clinic.selectedClinic?.clinic_id,
  )
  const canEdit = hasStaffScope(StaffScope.SETTINGS_WRITE)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [packetId, setPacketId] = useState<string | undefined>()
  const [packetName, setPacketName] = useState(DEFAULT_PACKET_NAME)
  const [packetDescription, setPacketDescription] = useState(
    DEFAULT_PACKET_DESCRIPTION,
  )
  const [isKioskDefault, setIsKioskDefault] = useState(false)
  const [templateKeys, setTemplateKeys] = useState<string[]>([])
  const [serverItems, setServerItems] = useState<FormGroupItem[]>([])
  const [templates, setTemplates] = useState<IFormTemplate[]>([])
  const [addOpen, setAddOpen] = useState(false)
  const [addSearch, setAddSearch] = useState('')
  const [addPage, setAddPage] = useState(1)
  const [addPageSize, setAddPageSize] = useState(10)
  const [addSortColumn, setAddSortColumn] = useState<string | undefined>(
    'title',
  )
  const [addSortDirection, setAddSortDirection] =
    useState<DataTableSortDirection>('asc')
  const [removeCandidate, setRemoveCandidate] = useState<PacketFormRow | null>(
    null,
  )
  const busyRef = useRef(false)

  useSettingsSubpageLoading(loading)

  const rememberGroup = useCallback((group: FormGroup): void => {
    setPacketId(group.id)
    setPacketName(group.name)
    setPacketDescription(group.description ?? DEFAULT_PACKET_DESCRIPTION)
    setIsKioskDefault(group.isNewPatientKioskDefault)
    setTemplateKeys(group.items.map((item) => item.templateKey))
    setServerItems(group.items)
  }, [])

  const load = useCallback(async (): Promise<void> => {
    if (!clinicId) {
      setPacketId(undefined)
      setTemplateKeys([])
      setServerItems([])
      setTemplates([])
      setIsKioskDefault(false)
      setLoading(false)
      return
    }
    setLoading(true)
    try {
      const [nextGroups, nextTemplates] = await Promise.all([
        getFormGroups(),
        getFormTemplates({ status: FormTemplateStatus.Approved, limit: 500 }),
      ])
      const group = chooseKioskGroup(nextGroups)
      if (group) rememberGroup(group)
      else {
        setPacketId(undefined)
        setPacketName(DEFAULT_PACKET_NAME)
        setPacketDescription(DEFAULT_PACKET_DESCRIPTION)
        setIsKioskDefault(false)
        setTemplateKeys([])
        setServerItems([])
      }
      setTemplates(nextTemplates)
    } catch {
      showToast(
        createFailedToast('Patient forms', 'Unable to load the kiosk forms.'),
      )
    } finally {
      setLoading(false)
    }
  }, [clinicId, rememberGroup, showToast])

  useEffect(() => {
    void load()
  }, [load])

  const openAdd = useCallback((): void => {
    setAddSearch('')
    setAddPage(1)
    setAddOpen(true)
  }, [])

  useEffect(() => {
    if (!canEdit) {
      setHeaderButtons(null)
      return () => setHeaderButtons(null)
    }
    setHeaderButtons(
      <Button
        className="settings-ds-button"
        variant="primary"
        size="md"
        type="button"
        onClick={openAdd}
        disabled={busy}
      >
        Add form
      </Button>,
    )
    return () => setHeaderButtons(null)
  }, [busy, canEdit, openAdd, setHeaderButtons])

  const templateByKey = useMemo(
    () =>
      new Map(
        templates.flatMap((template) =>
          template.template_key
            ? [[template.template_key, template] as const]
            : [],
        ),
      ),
    [templates],
  )
  const savedByKey = useMemo(
    () => new Map(serverItems.map((item) => [item.templateKey, item])),
    [serverItems],
  )

  const rows = useMemo((): PacketFormRow[] => {
    return templateKeys.map((key, index) => {
      const template = templateByKey.get(key)
      const saved = savedByKey.get(key)
      return {
        id: key,
        templateKey: key,
        order: index + 1,
        title: template?.title ?? saved?.title ?? 'Unavailable approved form',
        category: template?.category_title || '—',
        versionLabel: versionLabel(template?.version ?? saved?.version),
        available: template != null || saved?.available === true,
      }
    })
  }, [savedByKey, templateByKey, templateKeys])

  const availableRows = useMemo((): ApprovedFormRow[] => {
    const selected = new Set(templateKeys)
    const query = addSearch.trim().toLowerCase()
    return templates.flatMap((template) => {
      if (!template.template_key || selected.has(template.template_key)) {
        return []
      }
      const category = template.category_title ?? ''
      const matches =
        !query ||
        template.title.toLowerCase().includes(query) ||
        category.toLowerCase().includes(query)
      if (!matches) return []
      return [
        {
          id: template.template_key,
          templateKey: template.template_key,
          title: template.title,
          category: category || '—',
          versionLabel: versionLabel(template.version),
        },
      ]
    })
  }, [addSearch, templateKeys, templates])

  const persist = useCallback(
    async (nextKeys: string[], reason: PersistReason): Promise<boolean> => {
      if (busyRef.current) return false
      const previousKeys = templateKeys
      busyRef.current = true
      setBusy(true)
      setTemplateKeys(nextKeys)
      try {
        if (nextKeys.length === 0) {
          if (packetId) {
            if (isKioskDefault) await setNewPatientKioskFormGroup(null)
            await deleteFormGroup(packetId)
          }
          setPacketId(undefined)
          setIsKioskDefault(false)
          setServerItems([])
        } else {
          const payload = {
            name: packetName,
            description: packetDescription,
            templateKeys: nextKeys,
          }
          const saved = packetId
            ? await updateFormGroup(packetId, payload)
            : await createFormGroup(payload)
          rememberGroup(saved)
          if (!saved.isNewPatientKioskDefault) {
            await setNewPatientKioskFormGroup(saved.id)
            setIsKioskDefault(true)
          }
        }
        const successMessage =
          reason === 'add'
            ? 'The form was added to the signing order.'
            : reason === 'remove'
              ? 'The form was removed from the signing order.'
              : 'Signing order updated.'
        showToast(createSuccessToast('Patient forms', successMessage))
        return true
      } catch {
        setTemplateKeys(previousKeys)
        showToast(
          createFailedToast(
            'Patient forms',
            'Unable to update the forms patients sign after kiosk registration.',
          ),
        )
        return false
      } finally {
        busyRef.current = false
        setBusy(false)
      }
    },
    [
      isKioskDefault,
      packetDescription,
      packetId,
      packetName,
      rememberGroup,
      showToast,
      templateKeys,
    ],
  )

  const moveItem = useCallback(
    (from: number, to: number): void => {
      if (!canEdit || to < 0 || to >= templateKeys.length || from === to) return
      const next = [...templateKeys]
      const [moved] = next.splice(from, 1)
      if (!moved) return
      next.splice(to, 0, moved)
      void persist(next, 'reorder')
    },
    [canEdit, persist, templateKeys],
  )

  const sortedAvailableRows = useMemo((): ApprovedFormRow[] => {
    if (!addSortColumn) return availableRows
    const copy = [...availableRows]
    copy.sort((left, right) => {
      const leftValue =
        addSortColumn === 'category' ? left.category : left.title
      const rightValue =
        addSortColumn === 'category' ? right.category : right.title
      const compared = leftValue.localeCompare(rightValue, undefined, {
        sensitivity: 'base',
      })
      return addSortDirection === 'asc' ? compared : -compared
    })
    return copy
  }, [addSortColumn, addSortDirection, availableRows])

  const addTotalPages = Math.max(
    1,
    Math.ceil(sortedAvailableRows.length / addPageSize),
  )
  const addPageRows = sortedAvailableRows.slice(
    (addPage - 1) * addPageSize,
    addPage * addPageSize,
  )

  useEffect(() => {
    if (addPage > addTotalPages) setAddPage(addTotalPages)
  }, [addPage, addTotalPages])

  const handleAddSort = (columnId: string): void => {
    setAddPage(1)
    setAddSortDirection((direction) =>
      addSortColumn === columnId && direction === 'asc' ? 'desc' : 'asc',
    )
    setAddSortColumn(columnId)
  }

  const packetColumns = useMemo(
    (): DataTableColumn<PacketFormRow>[] => [
      { id: 'order', label: 'Order', width: 90, align: 'center' },
      {
        id: 'title',
        label: 'Form',
        renderCell: (row): React.ReactElement => (
          <span
            className={
              row.available ? undefined : 'patient-form-groups__unavailable'
            }
          >
            {row.title}
          </span>
        ),
      },
      { id: 'category', label: 'Category' },
      { id: 'versionLabel', label: 'Version', width: 100 },
    ],
    [],
  )

  const addColumns = useMemo(
    (): DataTableColumn<ApprovedFormRow>[] => [
      { id: 'title', label: 'Form', sortable: true },
      { id: 'category', label: 'Category', sortable: true },
      { id: 'versionLabel', label: 'Version', width: 100 },
    ],
    [],
  )

  const renderPacketActions = (row: PacketFormRow): React.ReactElement => {
    const index = templateKeys.indexOf(row.templateKey)
    return (
      <ActionButtons
        actions={[
          {
            id: 'up',
            icon: (
              <ArrowUpIcon
                className="ds-action-button__svg"
                aria-hidden="true"
              />
            ),
            label: `Move ${row.title} up`,
            tone: 'primary',
            variant: 'outline',
            disabled: busy || index <= 0,
            onClick: () => moveItem(index, index - 1),
          },
          {
            id: 'down',
            icon: (
              <ArrowDownIcon
                className="ds-action-button__svg"
                aria-hidden="true"
              />
            ),
            label: `Move ${row.title} down`,
            tone: 'primary',
            variant: 'outline',
            disabled: busy || index < 0 || index >= templateKeys.length - 1,
            onClick: () => moveItem(index, index + 1),
          },
          {
            id: 'remove',
            icon: (
              <DeleteIcon
                className="ds-action-button__svg"
                aria-hidden="true"
              />
            ),
            label: `Remove ${row.title}`,
            tone: 'danger',
            variant: 'outline',
            disabled: busy,
            onClick: () => setRemoveCandidate(row),
          },
        ]}
      />
    )
  }

  const confirmRemove = async (): Promise<void> => {
    if (!removeCandidate) return
    const removed = await persist(
      templateKeys.filter((key) => key !== removeCandidate.templateKey),
      'remove',
    )
    if (removed) setRemoveCandidate(null)
  }

  const addEmptyMessage = addSearch.trim()
    ? 'No approved forms match that search.'
    : 'Every approved form is already in the signing order.'

  return (
    <div className="patient-form-groups">
      <p className="patient-form-groups__intro">
        {canEdit
          ? 'These forms are signed in this order after a patient registers at the kiosk. Use the arrows to change the order.'
          : 'These forms are signed in this order after a patient registers at the kiosk.'}
      </p>
      <DataTable<PacketFormRow>
        className="patient-form-groups__table"
        columns={packetColumns}
        rows={rows}
        state={rows.length === 0 ? 'empty' : 'default'}
        emptyState={{
          title: 'No forms to sign',
          description:
            'Add approved forms. Patients sign them in the order shown here after kiosk registration.',
          actionLabel: canEdit ? 'Add form' : undefined,
          onAction: canEdit ? openAdd : undefined,
        }}
        showActions={canEdit}
        actionsLabel="Actions"
        renderActions={canEdit ? renderPacketActions : undefined}
      />

      <Modal
        open={addOpen}
        onClose={() => !busy && setAddOpen(false)}
        size="lg"
        title="Add form"
        bodyClassName="patient-form-groups__add-body"
        primaryLabel="Done"
        primaryDisabled={busy}
        onPrimaryClick={() => setAddOpen(false)}
      >
        <p className="patient-form-groups__intro">
          Approved forms only. A form is added at the end of the signing order.
        </p>
        <DataTable<ApprovedFormRow>
          className="patient-form-groups__table"
          columns={addColumns}
          rows={addPageRows}
          state={sortedAvailableRows.length === 0 ? 'empty' : 'default'}
          emptyState={{
            title: 'No forms to add',
            description: addEmptyMessage,
          }}
          showActions
          actionsLabel="Actions"
          renderActions={(row): React.ReactElement => (
            <Button
              size="sm"
              type="button"
              disabled={busy}
              onClick={() =>
                void persist([...templateKeys, row.templateKey], 'add')
              }
            >
              Add
            </Button>
          )}
          search={{
            value: addSearch,
            placeholder: 'Search by title or category',
            ariaLabel: 'Search approved forms',
            onChange: (value): void => {
              setAddSearch(value)
              setAddPage(1)
            },
          }}
          sortColumnId={addSortColumn}
          sortDirection={addSortColumn ? addSortDirection : undefined}
          onSort={handleAddSort}
          pagination={{
            currentPage: addPage,
            totalPages: addTotalPages,
            pageSize: addPageSize,
            onPageChange: setAddPage,
            onPageSizeChange: (size): void => {
              setAddPageSize(size)
              setAddPage(1)
            },
          }}
        />
      </Modal>

      <DeleteConfirmationModal
        show={removeCandidate != null}
        onHide={() => setRemoveCandidate(null)}
        onConfirm={() => void confirmRemove()}
        title="Remove form?"
        bodyText={
          removeCandidate
            ? `“${removeCandidate.title}” will no longer be signed after kiosk registration. The form itself stays available.`
            : undefined
        }
        confirmButtonText="Remove"
        confirmDisabled={busy}
      />
    </div>
  )
}

export default PatientFormGroups
